import React, { useEffect, useRef, useState } from 'react';

export interface WheelSlice {
  label: string;
  color: string;
  textColor?: string;
}

interface PieWheelProps {
  slices: WheelSlice[];
  /** Index of the slice the wheel must land on — decided by the server. */
  targetIndex: number | null;
  /** Increment to re-trigger spin animation even if targetIndex repeats. */
  spinToken: number;
  size?: number;
  onSpinComplete?: () => void;
}

const SPIN_DURATION_MS = 3200;

export const PieWheel: React.FC<PieWheelProps> = ({
  slices,
  targetIndex,
  spinToken,
  size = 260,
  onSpinComplete,
}) => {
  const [rotation, setRotation] = useState(0);
  const [spinning, setSpinning] = useState(false);
  const prevToken = useRef(spinToken);

  const n = slices.length;
  const anglePer = 360 / n;
  const r = size / 2;

  useEffect(() => {
    if (targetIndex === null) return;
    if (spinToken === prevToken.current && rotation !== 0) return;
    prevToken.current = spinToken;

    const sliceCenter = targetIndex * anglePer + anglePer / 2;
    const extraSpins = 5 * 360;
    const finalRotation = extraSpins + (360 - sliceCenter);

    setSpinning(true);
    requestAnimationFrame(() => {
      setRotation((prev) => prev - (prev % 360) + finalRotation);
    });

    const t = setTimeout(() => {
      setSpinning(false);
      onSpinComplete?.();
    }, SPIN_DURATION_MS);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetIndex, spinToken]);

  const cx = r;
  const cy = r;

  function polar(radius: number, angleDeg: number) {
    const a = ((angleDeg - 90) * Math.PI) / 180;
    return [cx + radius * Math.cos(a), cy + radius * Math.sin(a)] as const;
  }

  return (
    <div style={{ position: 'relative', width: size, height: size, margin: '0 auto' }}>
      {/* Pointer */}
      <div
        style={{
          position: 'absolute',
          top: -8,
          left: '50%',
          transform: 'translateX(-50%)',
          width: 0,
          height: 0,
          borderLeft: '13px solid transparent',
          borderRight: '13px solid transparent',
          borderTop: '20px solid var(--danger, #D9472B)',
          zIndex: 10,
          filter: 'drop-shadow(0 2px 2px rgba(0,0,0,.2))',
        }}
      />
      <svg
        width={size}
        height={size}
        viewBox={`0 0 ${size} ${size}`}
        style={{
          borderRadius: '50%',
          boxShadow: '0 0 0 6px var(--primary, #F5C21A), 0 4px 16px rgba(0,0,0,.18)',
          transform: `rotate(${rotation}deg)`,
          transition: spinning
            ? `transform ${SPIN_DURATION_MS}ms cubic-bezier(0.17, 0.85, 0.28, 1)`
            : 'none',
        }}
      >
        {slices.map((slice, i) => {
          const a0 = i * anglePer;
          const a1 = (i + 1) * anglePer;
          const [x0, y0] = polar(r - 3, a0);
          const [x1, y1] = polar(r - 3, a1);
          const largeArc = a1 - a0 > 180 ? 1 : 0;
          const path = `M${cx},${cy} L${x0},${y0} A${r - 3},${r - 3} 0 ${largeArc} 1 ${x1},${y1} Z`;
          const mid = (a0 + a1) / 2;
          const [tx, ty] = polar(r * 0.6, mid);
          const lines = slice.label.split(' ');
          return (
            <g key={i}>
              <path d={path} fill={slice.color} stroke="#FFFFFF" strokeWidth={2} />
              <text
                x={tx}
                y={ty}
                fill={slice.textColor ?? '#231C0E'}
                fontFamily="Manrope, sans-serif"
                fontWeight={700}
                fontSize={11}
                textAnchor="middle"
              >
                {lines.map((ln, li) => (
                  <tspan key={li} x={tx} dy={li === 0 ? 0 : 12}>
                    {ln}
                  </tspan>
                ))}
              </text>
            </g>
          );
        })}
      </svg>
      {/* Hub */}
      <div
        style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 46,
          height: 46,
          borderRadius: '50%',
          background: '#FFFFFF',
          border: '4px solid var(--primary, #F5C21A)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 18,
          boxShadow: '0 2px 8px rgba(0,0,0,.12)',
        }}
      >
        🎯
      </div>
    </div>
  );
};

export const DEFAULT_SLICE_COLORS = [
  '#D9472B', '#FAE7B8', '#F5C21A', '#2E7D5B', '#E8582F', '#FCEFCB', '#F0A93A', '#4E9C79',
];
export const DEFAULT_SLICE_TEXT_COLORS = [
  '#FFFFFF', '#231C0E', '#231C0E', '#FFFFFF', '#FFFFFF', '#231C0E', '#231C0E', '#FFFFFF',
];
